import { FilterAlt, RestorePage, ViewColumn, Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonGroup,
  ClickAwayListener,
  Divider,
  IconButton,
  Drawer as MuiDrawer,
  SvgIconProps,
  Tooltip,
} from '@mui/material';
import clsx from 'clsx';
import { useColumnsRef } from 'components/Table/controls/ColumnControls/useColumnRef';
import { CloseButton } from 'components/buttons/CloseButton';
import { useUserTenantPreference } from 'kineticUtils';
import { ComponentType, memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnControls } from './ColumnControls';
import { Filters } from './Filters';
import { SavedFiltersButton } from './SavedFilters';
import {
  CombinedTableControlsContextValue,
  useCombinedTableControlsContext,
  useControlsDrawerContext,
} from './contexts/context';
import { useSavedFilterContext } from './contexts/savedFiltersContext';
import { useUpdateColumnsFromPreference } from './hooks/useUpdateColumnsFromPreference';
import { useCombinedTableControlsStyles } from './styles';

const DrawerContentButton = ({
  name,
  icon: Icon,
}: {
  name: CombinedTableControlsContextValue['activeContent'];
  icon: ComponentType<{ fontSize: SvgIconProps['fontSize'] }>;
}) => {
  const { activeContent, setActiveContent } = useCombinedTableControlsContext();

  return (
    <Button
      size='small'
      onClick={() => {
        setActiveContent(name);
      }}
      className={clsx({ active: activeContent === name })}
    >
      <Icon fontSize='small' />
    </Button>
  );
};

const ShowHideChipsButton = () => {
  const { t } = useTranslation('common');
  const { filterChips } = useSavedFilterContext();
  const Icon = filterChips.isShown ? VisibilityOff : Visibility;
  const onClick = filterChips.isShown ? filterChips.hide : filterChips.show;
  const title = filterChips.isShown ? 'hideFilterChipsTooltip' : 'showFilterChipsTooltip';
  return (
    <Tooltip title={t(title)}>
      <IconButton size='small' onClick={onClick}>
        <Icon fontSize='small' />
      </IconButton>
    </Tooltip>
  );
};

const CloseDrawerButton = () => {
  const { close } = useControlsDrawerContext();
  return <CloseButton onClick={close} />;
};

const ResetColumnsButton = () => {
  const { columnsPreferenceKey } = useCombinedTableControlsContext();
  const { t } = useTranslation('common');

  const { clearPreference } = useUserTenantPreference({ key: columnsPreferenceKey });
  const clearAndRefresh = async () => {
    await clearPreference();
    window.location.reload();
  };

  return (
    <Tooltip title={t('resetColumnsTooltip')}>
      <IconButton size='small' onClick={clearAndRefresh}>
        <RestorePage fontSize='small' />
      </IconButton>
    </Tooltip>
  );
};

const Header = () => {
  const { filters, columns, activeContent } = useCombinedTableControlsContext();
  const classes = useCombinedTableControlsStyles();
  return (
    <Box padding={2} display='flex'>
      <ButtonGroup className={classes.buttonGroup}>
        {filters && <DrawerContentButton name='filters' icon={FilterAlt} />}
        {columns && <DrawerContentButton name='columns' icon={ViewColumn} />}
      </ButtonGroup>
      <Box marginLeft='auto'>
        {filters && <SavedFiltersButton />}
        {filters && <ShowHideChipsButton />}
        {activeContent === 'columns' && <ResetColumnsButton />}
        <CloseDrawerButton />
      </Box>
    </Box>
  );
};

const DrawerContent = () => {
  const { activeContent, columns, filters, columnsPreferenceKey, columnsRef } =
    useCombinedTableControlsContext();

  // still expose the columns, even if we arent rendering the ColumnControls
  useColumnsRef({ columnsRef, skip: !!columnsPreferenceKey });

  const {
    savedFilters: { preferenceColumns },
  } = useSavedFilterContext();

  const { updateColumnsFromPreference } = useUpdateColumnsFromPreference();
  useEffect(() => {
    if (preferenceColumns) {
      updateColumnsFromPreference(preferenceColumns);
    }
  }, [preferenceColumns, updateColumnsFromPreference]);

  return (
    <>
      <Box sx={{ display: activeContent === 'columns' ? 'block' : 'none' }}>
        {!!columns ? (
          <ColumnControls columnsRef={columnsRef} preferenceKey={columnsPreferenceKey!} />
        ) : null}
      </Box>
      <Box
        sx={{
          display: activeContent === 'filters' ? 'block' : 'none',
          paddingLeft: 2,
          paddingRight: 2,
          paddingTop: 1,
        }}
      >
        {!!filters && <Filters />}
      </Box>
    </>
  );
};

export const Drawer = memo(() => {
  const { close, isOpen } = useControlsDrawerContext();
  const classes = useCombinedTableControlsStyles();

  return (
    <ClickAwayListener mouseEvent='onMouseUp' touchEvent='onTouchEnd' onClickAway={close}>
      <MuiDrawer
        classes={{ paper: classes.drawer }}
        anchor='right'
        open={isOpen}
        onClose={close}
        variant='persistent'
        ModalProps={{ disablePortal: true }}
      >
        <Header />
        <Divider className={classes.divider} />
        <DrawerContent />
      </MuiDrawer>
    </ClickAwayListener>
  );
});
export const ControlsDrawer = Drawer;
